import type { ActiveOrder } from '@/resources/types'
import { DeliveryType } from '@/resources/enums'
import { isToday } from '@/resources/utils/global/dates'

export const MIN_ACTIVE_ORDERS_SEARCH_LENGTH = 3

export const KANBAN_CBE_FILTER_CHIP_IDS = {
  D0: 'd0',
  SCHEDULED: 'scheduled',
  PICKUP: 'pickup',
} as const

export type KanbanCbeFilterChipId =
  (typeof KANBAN_CBE_FILTER_CHIP_IDS)[keyof typeof KANBAN_CBE_FILTER_CHIP_IDS]

const KANBAN_CBE_CHIP_TO_DELIVERY_TYPE: Partial<
  Record<KanbanCbeFilterChipId, DeliveryType>
> = {
  [KANBAN_CBE_FILTER_CHIP_IDS.SCHEDULED]: DeliveryType.RECEIVE,
  [KANBAN_CBE_FILTER_CHIP_IDS.PICKUP]: DeliveryType.PICKUP,
}

export type ActiveOrderKanbanCbeFilterInput = Pick<ActiveOrder, 'delivery'>

export const ACTIVE_ORDERS_SEARCH_DEBOUNCE_MS = 1500

export const toActiveOrdersSearchParam = (input: string): string | null => {
  const trimmed = input.trim()
  return trimmed.length >= MIN_ACTIVE_ORDERS_SEARCH_LENGTH ? trimmed : null
}

export const shouldShowKanbanSearchMinLengthHint = (input: string): boolean => {
  const trimmedLen = input.trim().length
  return trimmedLen > 0 && trimmedLen < MIN_ACTIVE_ORDERS_SEARCH_LENGTH
}

export const hasKanbanFiltersAppliedToQuery = (
  isSellerKanbanFilterEnabled: boolean,
  debouncedSearchInput: string,
  kanbanFilterDeliveryType: number | null
): boolean =>
  isSellerKanbanFilterEnabled &&
  (toActiveOrdersSearchParam(debouncedSearchInput) !== null ||
    kanbanFilterDeliveryType !== null)

export const hasServerSideKanbanFilterApplied = hasKanbanFiltersAppliedToQuery

export const resolveKanbanDeliveryTypeAfterChipClick = (
  currentFilter: number | null,
  chipApiValue: number | null
): number | null => {
  if (chipApiValue === null) {
    return null
  }
  if (currentFilter === chipApiValue) {
    return null
  }
  return chipApiValue
}

export const isActiveOrderMatchingKanbanCbeD0Filter = (
  order: ActiveOrderKanbanCbeFilterInput
): boolean => {
  const startDate = order.delivery?.deliveryDate?.startDate

  if (!startDate) {
    return false
  }

  return isToday(startDate)
}

export const resolveKanbanCbeChipDeliveryType = (
  chipId: string | null
): DeliveryType | null => {
  if (!chipId) {
    return null
  }

  return (
    KANBAN_CBE_CHIP_TO_DELIVERY_TYPE[chipId as KanbanCbeFilterChipId] ?? null
  )
}

export const isActiveOrderMatchingKanbanCbeDeliveryTypeFilter = (
  order: ActiveOrderKanbanCbeFilterInput,
  deliveryType: DeliveryType
): boolean => order.delivery?.type === deliveryType

export const filterActiveOrdersByKanbanCbeChip = <T extends ActiveOrder>(
  orders: T[],
  chipId: string | null
): T[] => {
  if (chipId === KANBAN_CBE_FILTER_CHIP_IDS.D0) {
    return orders.filter(isActiveOrderMatchingKanbanCbeD0Filter)
  }

  const deliveryType = resolveKanbanCbeChipDeliveryType(chipId)

  if (deliveryType) {
    return orders.filter(order =>
      isActiveOrderMatchingKanbanCbeDeliveryTypeFilter(order, deliveryType)
    )
  }

  return orders
}
